// B1 (2026-10-29) — `organisation.brandName`, the word beside the logo in the
// sidebar and the mobile bar.
//
// ⚠ WHAT IT DOES NOT MEAN. It is not the organisation's name (`organisationName`
// is that, and goes on invoices), and it is not the product's name anywhere
// else: the browser tab title and every email still say "Rosterem". It is
// display text for two nav surfaces, nothing more.
//
// Stored: a string of 1–24 characters, already trimmed, with no control
// characters. ABSENT means "Rosterem" — clearing the field deletes the key, so
// there is no stored empty string and no stored null. firestore.rules holds the
// same bounds (src/tests/rules/orgBrandNameGate.rules.test.js), manager-only.
//
// "Characters" are CODE POINTS (`Array.from`), not UTF-16 units: the rules'
// regex counts code points, so `.length` would refuse an emoji name the
// rules allow.
import { reactive } from 'vue';

export const DEFAULT_BRAND_NAME = 'Rosterem';
export const BRAND_NAME_MAX = 24;

// C0 controls, DEL and C1 controls — line breaks and tabs among them.
const CONTROL = /[\u0000-\u001F\u007F-\u009F]/;

export const BRAND_NAME_TOO_LONG = `The name beside your logo can be up to ${BRAND_NAME_MAX} characters.`;
export const BRAND_NAME_CONTROL = 'The name beside your logo cannot contain line breaks, tabs or other control characters.';

// The Styling copy. It says where the name appears and, as the brief requires,
// where it does NOT: the tab title and emails are unchanged.
export const BRAND_NAME_HINT = 'Shown in the sidebar. The browser tab title and emails still say Rosterem. Leave it blank to show Rosterem.';

const codePoints = (s) => Array.from(s).length;

/**
 * What a typed value saves as: `{ brandName: string }`, `{ brandName: null }`
 * (blank — clear the field, the sidebar shows Rosterem) or `{ error }`.
 */
export function readBrandNameInput(raw) {
  const text = typeof raw === 'string' ? raw.trim() : '';
  if (text === '') return { brandName: null };
  if (CONTROL.test(text)) return { error: BRAND_NAME_CONTROL };
  if (codePoints(text) > BRAND_NAME_MAX) return { error: BRAND_NAME_TOO_LONG };
  return { brandName: text };
}

/** The stored name if the document carries a valid one, else null. Never throws. */
export function storedBrandName(org) {
  const v = org && typeof org === 'object' ? org.brandName : undefined;
  if (typeof v !== 'string') return null;
  const r = readBrandNameInput(v);
  // A stored value must already BE its normal form: untrimmed is not valid.
  return r.brandName !== undefined && r.brandName === v ? v : null;
}

/** The text the nav shows: the stored name, or "Rosterem". */
export function resolveBrandName(org) {
  return storedBrandName(org) ?? DEFAULT_BRAND_NAME;
}

// The value the Navbar shows, by organisation id. The Navbar fills it when it
// reads the org document; Settings → Styling updates it after a save, so the
// sidebar changes without a reload. Keyed by org so a different organisation's
// name is never shown while the next one loads.
export const orgBrandNames = reactive({});

export function setOrgBrandName(orgId, value) {
  if (!orgId) return;
  orgBrandNames[orgId] = typeof value === 'string' ? value : null;
}

export function brandNameForOrg(orgId) {
  return resolveBrandName({ brandName: orgId ? orgBrandNames[orgId] : undefined });
}
